import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// ── Pure calculation helpers — exported for unit testing ─────────────────────
export const calcCommittedVariance = (committed: number, budget: number) =>
  committed - budget;

export const calcTotalProfit = (jobValue: number, committed: number) =>
  jobValue - committed;

export const calcProfitMargin = (profit: number, jobValue: number) =>
  jobValue > 0 ? (profit / jobValue) * 100 : 0;

export const calcARBalance = (invoiced: number, received: number) =>
  invoiced - received;

export const calcPaymentsDue = (billed: number, paid: number) =>
  Math.max(0, billed - paid);

export const calcVarianceImpact = (budget: number, committed: number) =>
  budget - committed; // positive = saved money, negative = cost overrun

// ── Hook ─────────────────────────────────────────────────────────────────────
export const useProjectKPIData = (projectId: string | undefined) => {
  const { user } = useAuth();
  const enabled = !!user && !!projectId;

  // ── Card 1: Total Job Value ──────────────────────────────────────────────
  const contractsQ = useQuery({
    queryKey: ["kpi_contracts", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("contracts")
        .select("original_value, revised_value")
        .eq("project_id", projectId!);
      if (error) throw error;
      return data ?? [];
    },
    enabled,
  });

  // Derive addendums/deductions from SOV lines with source_type = 'change_order'
  const sovCOLinesQ = useQuery({
    queryKey: ["kpi_sov_co_lines", projectId],
    queryFn: async () => {
      // Get all contract IDs for this project
      const { data: contractRows, error: cErr } = await supabase
        .from("contracts")
        .select("id")
        .eq("project_id", projectId!);
      if (cErr) throw cErr;
      const contractIds = (contractRows ?? []).map((c) => c.id);
      if (contractIds.length === 0) return [];

      // Fetch ALL SOV lines, then separate by source_type on the client
      const { data, error } = await supabase
        .from("sov_lines")
        .select("scheduled_value, source_type")
        .in("contract_id", contractIds);
      if (error) throw error;
      return (data ?? []) as { scheduled_value: number; source_type: string | null }[];
    },
    enabled,
  });

  // ── Card 2: SOV Budget & Committed Cost ──────────────────────────────────
  const sovBudgetQ = useQuery({
    queryKey: ["kpi_sov_budget", projectId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_project_estimated_cost", {
        p_project_id: projectId!,
      } as any);
      if (error) throw error;
      return Number(data ?? 0);
    },
    enabled,
  });

  const committedCostQ = useQuery({
    queryKey: ["kpi_committed_cost", projectId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_committed_cost", {
        p_project_id: projectId!,
      });
      if (error) throw error;
      return Number(data ?? 0);
    },
    enabled,
  });

  // ── Card 2b: Subcontractor Variance (assigned lines only) ─────────────
  const subVarianceQ = useQuery({
    queryKey: ["kpi_sub_variance", projectId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_subcontractor_variance" as any, {
        p_project_id: projectId!,
      });
      if (error) throw error;
      return Number(data ?? 0);
    },
    enabled,
  });

  // ── Card 3 AR: Invoices ───────────────────────────────────────────────────
  const invoicesQ = useQuery({
    queryKey: ["kpi_invoices", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices")
        .select("total, amount_paid, status")
        .eq("project_id", projectId!)
        .is("deleted_at", null)
        .neq("status", "void" as any);
      if (error) throw error;
      return (data ?? []) as { total: number; amount_paid: number | null; status: string }[];
    },
    enabled,
  });

  // ── Card 3 AP: Bills ─────────────────────────────────────────────────────
  const billsQ = useQuery({
    queryKey: ["kpi_bills", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bills")
        .select("total, amount_paid, status, paid_at")
        .eq("project_id", projectId!)
        .not("status", "in", '("void","voided","draft","rejected")');
      if (error) throw error;
      return (data ?? []) as { total: number; amount_paid: number | null; status: string; paid_at: string | null }[];
    },
    enabled,
  });

  // ── Derived values ────────────────────────────────────────────────────────
  const contracts = contractsQ.data ?? [];
  const allSovLines = sovCOLinesQ.data ?? [];

  // Card 1 — use revised_value as the true contract total, derive CO impact
  const originalContract = contracts.reduce((s, c) => s + Number(c.original_value), 0);
  const revisedTotal = contracts.reduce((s, c) => s + Number(c.revised_value), 0);
  // CO lines are those explicitly tagged as change_order
  const coLines = allSovLines.filter((l) => l.source_type === "change_order");
  const coPositive = coLines
    .filter((l) => Number(l.scheduled_value) > 0)
    .reduce((s, l) => s + Number(l.scheduled_value), 0);
  const coNegative = Math.abs(
    coLines
      .filter((l) => Number(l.scheduled_value) < 0)
      .reduce((s, l) => s + Number(l.scheduled_value), 0)
  );
  // If CO lines exist use them; otherwise derive from revised - original
  const coNetFromLines = coPositive - coNegative;
  const coNetFromContract = revisedTotal - originalContract;
  const coNet = coLines.length > 0 ? coNetFromLines : coNetFromContract;
  const addendums = coLines.length > 0 ? coPositive : Math.max(0, coNetFromContract);
  const deductions = coLines.length > 0 ? coNegative : Math.abs(Math.min(0, coNetFromContract));
  const totalJobValue = revisedTotal > 0 ? revisedTotal : originalContract;

  // Card 2
  const sovBudget = sovBudgetQ.data ?? 0;
  const committedCost = committedCostQ.data ?? 0;
  // Subcontractor variance: (budgeted_sub_rate - actual_wo_rate) * assigned_qty
  // Positive = under budget (saved), Negative = over budget (overspend)
  const committedVariance = subVarianceQ.data ?? 0;

  // Card 3 AR
  // NOTE: There is no `invoice_payments` table — `invoices.amount_paid` is the
  // canonical received-cash rollup (mirrors `bills.amount_paid` on AP side).
  // We restrict the sum to AR_BILLABLE statuses so any stray amount_paid on
  // draft/void invoices cannot inflate the AR balance.
  const AR_BILLABLE = ["approved", "sent", "paid", "partially_paid", "overdue"];
  const invoices = invoicesQ.data ?? [];
  const billableInvoices = invoices.filter((i) => AR_BILLABLE.includes(i.status));
  const totalInvoiced = billableInvoices.reduce((s, i) => s + Number(i.total), 0);
  const paymentsReceived = billableInvoices.reduce((s, i) => s + Number(i.amount_paid ?? 0), 0);
  const arBalance = calcARBalance(totalInvoiced, paymentsReceived);

  // Card 3 AP
  const bills = billsQ.data ?? [];
  const totalVendorBilled = bills.reduce((s, b) => s + Number(b.total), 0);
  const vendorPaid = bills
    .filter((b) => b.paid_at !== null)
    .reduce((s, b) => s + Number(b.amount_paid ?? b.total), 0);
  const paymentsDue = calcPaymentsDue(totalVendorBilled, vendorPaid);

  // Card 4
  const totalProfit = calcTotalProfit(totalJobValue, committedCost);
  const profitMargin = calcProfitMargin(totalProfit, totalJobValue);
  const baseProfit = originalContract - sovBudget;
  const coImpact = addendums - deductions;
  const varianceImpact = committedVariance; // same as sub variance — positive = saved

  return {
    // Card 1
    originalContract,
    addendums,
    deductions,
    totalJobValue,
    // Card 2
    sovBudget,
    committedCost,
    committedVariance,
    // Card 3 AR
    totalInvoiced,
    paymentsReceived,
    arBalance,
    // Card 3 AP
    totalVendorBilled,
    vendorPaid,
    paymentsDue,
    // Card 4
    totalProfit,
    profitMargin,
    baseProfit,
    coImpact,
    varianceImpact,
    // Meta
    isLoading:
      contractsQ.isLoading ||
      sovCOLinesQ.isLoading ||
      sovBudgetQ.isLoading ||
      committedCostQ.isLoading ||
      subVarianceQ.isLoading ||
      invoicesQ.isLoading ||
      billsQ.isLoading,
  };
};
